// src/models/egresoDetModel.js

import pb from '../lib/pocketbase.js';

// Obtener los detalles de egreso paginados
export async function getEgresoDets(egresoCabId, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoDet').getList(page, perPage, {
      filter: `egresoCab = "${egresoCabId}"`,
      $autoCancel: false
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      page: response.currentPage,
    };
  } catch (error) {
    console.error("Error fetching egresoDet:", error);
    throw new Error("Error al obtener los detalles de egreso");
  }
}

// Crear un nuevo detalle de egreso
export async function createEgresoDet(egresoDet) {
  try {
    const response = await pb.collection('egresoDet').create(egresoDet);
    return response;
  } catch (error) {
    console.error('Error creating egresoDet:', error);
    throw new Error(formatPbError(error, 'Error al crear el detalle de egreso'));
  }
}

// Actualizar un detalle de egreso
export async function updateEgresoDet(id, egresoDet) {
  try {
    const response = await pb.collection('egresoDet').update(id, egresoDet);
    return response;
  } catch (error) {
    console.error("Error updating egresoDet:", error);
    throw new Error("Error al actualizar el detalle de egreso");
  }
}

// Eliminar un detalle de egreso
export async function deleteEgresoDet(id) {
  try {
    await pb.collection('egresoDet').delete(id);
  } catch (error) {
    console.error("Error deleting egresoDet:", error);
    throw new Error("Error al eliminar el detalle de egreso");
  }
}

function escapeFilterValue(value) {
  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function formatPbError(error, fallback) {
  const data = error?.response || error?.data;
  const fieldErrors = data?.data;

  if (fieldErrors && typeof fieldErrors === 'object') {
    const parts = Object.entries(fieldErrors).map(([field, detail]) => {
      if (typeof detail === 'string') return `${field}: ${detail}`;
      if (detail?.message) return `${field}: ${detail.message}`;
      return `${field}: inválido`;
    });
    if (parts.length) return parts.join(', ');
  }

  return data?.message || error?.message || fallback;
}

async function resolveBodegaForEgreso(egresoCabId) {
  const items = await pb.collection('egresoDet').getFullList({
    filter: `egresoCab = "${escapeFilterValue(egresoCabId)}"`,
    $autoCancel: false,
  });

  const fromSibling = items.find((row) => row.bodega)?.bodega;
  return fromSibling || null;
}

async function findEgresoDetByCode(egresoCabId, code) {
  const trimmed = code?.trim();
  if (!trimmed) {
    throw new Error('Ingrese un SKU o código de barras');
  }

  const escaped = escapeFilterValue(trimmed);
  const cabFilter = escapeFilterValue(egresoCabId);

  let items = await pb.collection('egresoDet').getFullList({
    filter: `egresoCab = "${cabFilter}" && sku = "${escaped}"`,
    $autoCancel: false,
  });

  if (!items.length) {
    try {
      items = await pb.collection('egresoDet').getFullList({
        filter: `egresoCab = "${cabFilter}" && codbar = "${escaped}"`,
        $autoCancel: false,
      });
    } catch {
      items = [];
    }
  }

  if (!items.length) {
    throw new Error(`SKU/código no encontrado: ${trimmed}`);
  }

  return items[0];
}

export async function incrementPickedBySku(egresoCabId, code, qty = 1) {
  const qtyNum = Math.max(1, Number(qty) || 1);
  const det = await findEgresoDetByCode(egresoCabId, code);
  const currentPicked = Number(det.picked) || 0;
  return updateEgresoDet(det.id, { picked: currentPicked + qtyNum });
}

export async function adjustPickedBySku(egresoCabId, code, delta) {
  const deltaNum = Number(delta) || 0;
  if (!deltaNum) {
    throw new Error('Cantidad inválida');
  }

  const det = await findEgresoDetByCode(egresoCabId, code);
  return adjustPicked(det.id, deltaNum);
}

export async function adjustPicked(egresoDetId, delta) {
  const deltaNum = Number(delta) || 0;
  if (!deltaNum) {
    throw new Error('Cantidad inválida');
  }

  const det = await pb.collection('egresoDet').getOne(egresoDetId, { $autoCancel: false });
  const currentPicked = Number(det.picked) || 0;
  const newPicked = Math.max(0, currentPicked + deltaNum);

  if (deltaNum < 0 && newPicked === currentPicked) {
    throw new Error('No hay unidades pickeadas para restar');
  }

  return updateEgresoDet(egresoDetId, { picked: newPicked });
}

export async function addOrUpdateEgresoDetLine(egresoCabId, { sku, descrip, cantidad, peso = 0, precio = 0, bodega = '' }) {
  const trimmedSku = sku?.trim();
  if (!trimmedSku) {
    throw new Error('SKU requerido');
  }

  const qty = Math.max(1, Number(cantidad) || 1);
  const cabFilter = escapeFilterValue(egresoCabId);
  const skuFilter = escapeFilterValue(trimmedSku);

  const existing = await pb.collection('egresoDet').getFullList({
    filter: `egresoCab = "${cabFilter}" && sku = "${skuFilter}"`,
    $autoCancel: false,
  });

  if (existing.length) {
    const det = existing[0];
    const newCantidad = (Number(det.cantidad) || 0) + qty;
    return updateEgresoDet(det.id, {
      cantidad: newCantidad,
      ...(descrip && !det.descrip ? { descrip } : {}),
    });
  }

  const bodegaId = bodega || await resolveBodegaForEgreso(egresoCabId);
  if (!bodegaId) {
    throw new Error('No se pudo determinar la bodega del pedido');
  }

  const linePrecio = Number(precio) || 0;
  if (linePrecio <= 0) {
    throw new Error('Precio requerido para agregar la línea');
  }

  return createEgresoDet({
    egresoCab: egresoCabId,
    bodega: bodegaId,
    sku: trimmedSku,
    descrip: descrip?.trim() || trimmedSku,
    cantidad: qty,
    picked: 0,
    peso: Number(peso) || 0,
    precio: linePrecio,
    picker: '',
    image_url: '',
  });
}

// Buscar detalles de egreso por descripción con paginación
export async function searchEgresoDets(query, egresoCabId, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoDet').getList(page, perPage, {
      filter: `descrip ~ "${query}" && egresoCabId = "${egresoCabId}"`,
    });
    return {
      items: response.items,
      totalItems: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error("Error searching egresoDets:", error);
    throw new Error("Error al buscar los detalles de egreso");
  }
}